import { createContext, useContext, type ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';

/** The source numbers of the surrounding AnswerCard, so a Citation can check it points at one. */
const SourceNumbers = createContext<Set<number> | null>(null);

export type AnswerSource = {
  id: string;
  /** Citation number shown in the text. */ n: number;
  label: ReactNode;
  href: string;
};

export type AnswerCardProps = {
  /** "Answer from our index". */
  eyebrow?: ReactNode;
  question: ReactNode;
  /** The answer; mark every claim with <Citation n={…} /> pointing at one of `sources`. */
  children?: ReactNode;
  /** When the index has no answer: said plainly, shown instead of an answer (no sources, no results). */
  noAnswer?: ReactNode;
  /** Follow-up actions (FilterChips or Buttons). */
  followUps?: ReactNode;
  sources?: AnswerSource[];
  /** "Sources". */
  sourcesLabel?: ReactNode;
  /** The cited jobs, as JobCards, under the answer. */
  results?: ReactNode;
  className?: string;
};

/** A numbered citation in an answer, pointing at a source listed under it. */
export function Citation({
  n,
  href,
  label,
}: {
  n: number;
  /** Link to the cited listing. */ href?: string;
  /** Accessible name ("Source 1: comatec.fi"). */ label?: string;
}) {
  const known = useContext(SourceNumbers);
  devWarn(
    known !== null && !known.has(n),
    `Citation ${n} has no matching source in this AnswerCard: no citation, no claim.`,
  );
  const cls =
    'mx-0.5 inline-grid size-4.5 place-items-center rounded-full bg-jc-forest-soft align-text-top font-display text-xs font-bold text-jc-accent';
  return href ? (
    <a
      href={href}
      aria-label={label}
      className={cx(cls, 'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus')}
    >
      {n}
    </a>
  ) : (
    <sup aria-label={label} className={cls}>
      {n}
    </sup>
  );
}

/**
 * An AI search answer grounded in the index: the question, a short answer whose every claim cites a
 * real listing, follow-up actions, the sources, then the cited jobs. No citation, no claim.
 */
export function AnswerCard({
  eyebrow,
  question,
  children,
  noAnswer,
  followUps,
  sources = [],
  sourcesLabel,
  results,
  className,
}: AnswerCardProps) {
  const numbers = new Set(sources.map((s) => s.n));
  return (
    <SourceNumbers.Provider value={numbers}>
      <section className={cx('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-3 rounded-jc-container border border-jc-card-border bg-jc-surface p-5 text-jc-ink shadow-jc-sm md:p-6">
          {eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{eyebrow}</span> : null}
          <h2 className="type-heading">{question}</h2>
          {noAnswer != null ? (
            <div role="status" className="rounded-jc-info bg-jc-elevated p-4 text-sm text-jc-ink-secondary">
              {noAnswer}
            </div>
          ) : (
            <div className="text-jc-prose text-jc-ink [&>p+p]:mt-3">{children}</div>
          )}
          {followUps ? <div className="flex flex-wrap gap-2">{followUps}</div> : null}
          {noAnswer == null && sources.length ? (
            <div className="flex flex-col gap-1 border-t border-jc-border pt-3 text-xs text-jc-ink-secondary">
              {sourcesLabel ? <span className="font-semibold uppercase tracking-wider">{sourcesLabel}</span> : null}
              <ol className="flex flex-col gap-1">
                {sources.map((s) => (
                  <li key={s.id} className="flex items-center gap-1.5">
                    <Citation n={s.n} />
                    <a
                      href={s.href}
                      className="truncate rounded-jc-info hover:text-jc-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
        {noAnswer == null && results ? <div className="flex flex-col gap-3">{results}</div> : null}
      </section>
    </SourceNumbers.Provider>
  );
}
